import { Flex, Focusable, Text, Tooltip, TooltipTrigger } from '@backstage/ui';
import type { RemixiconComponentType } from '@remixicon/react';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import { Meter } from '../home/Meter';
import { displayedPercent, usageLevel } from '../home/platform';
import { IconLabel } from '../ui';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { projectEnvironmentsTranslationRef } from './translation';
import {
  inMemoryUnit,
  memoryUnit,
  requestedOfLimit,
  usedOfLimit,
  type MemoryUnit,
  type ResourceFigures,
  type VolumeUsage,
} from './usage';

/** The tone of the table's gauge at each level (`.waadoo-env-gauge`). */
const GAUGE_TONES = {
  ok: 'normal',
  warning: 'warning',
  critical: 'critical',
} as const;

export type ResourceKind = 'cpu' | 'memory';

/** What `useUsageFormat` gives. */
export interface UsageFormat {
  percent(ratio: number): string;
  /** A figure without its unit, for a line that states the unit once. */
  bare(kind: ResourceKind, value: number, unit: MemoryUnit): string;
  unit(kind: ResourceKind, unit: MemoryUnit): string;
  /** A size in bytes, in the unit that suits it alone. */
  size(value: number): string;
}

/**
 * The figures of the resources cards in words: millicores as cores, bytes in
 * Mio or Gio, in the locale the page's words are in (`format.locale`, see
 * deployHistory.ts for why it is a message).
 */
export function useUsageFormat(): UsageFormat {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const locale = t('format.locale');
  // Two decimals for the cores: an idle environment uses a few millicores,
  // which one decimal would print as 0.
  const cores = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const bytes = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const percent = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  });
  const unitLabel = (unit: MemoryUnit) =>
    unit === 'GiB' ? t('resources.unitGiB') : t('resources.unitMiB');

  return {
    percent: (ratio: number) => percent.format(ratio),
    bare: (kind: ResourceKind, value: number, unit: MemoryUnit) =>
      kind === 'cpu'
        ? cores.format(value / 1000)
        : bytes.format(inMemoryUnit(value, unit)),
    unit: (kind: ResourceKind, unit: MemoryUnit) =>
      kind === 'cpu' ? t('resources.unitCores') : unitLabel(unit),
    size: (value: number) => {
      const unit = memoryUnit(value);
      return t('resources.quantity', {
        value: bytes.format(inMemoryUnit(value, unit)),
        unit: unitLabel(unit),
      });
    },
  };
}

/** One resource in words — see `useFiguresText`. */
export interface FiguresText {
  /** Used against the limit, used alone without a limit, or « indisponible ». */
  main: string;
  requested: string;
  /** The limit, or « illimitée ». */
  limit: string;
}

/**
 * One resource as words: used against the limit, or what can be said
 * without a bar — the words of `ResourceMeter`, `ResourceGauge` and the
 * environments card's total line.
 */
export function useFiguresText(): (
  kind: ResourceKind,
  figures: ResourceFigures,
) => FiguresText {
  const format = useUsageFormat();
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);

  return (kind, figures) => {
    const unit = memoryUnit(figures.used, figures.requested, figures.limit);
    const quantity = (value: number) =>
      t('resources.quantity', {
        value: format.bare(kind, value, unit),
        unit: format.unit(kind, unit),
      });
    let main: string;
    if (figures.used === null) {
      main = t('resources.usageUnavailable');
    } else if (figures.limit === null) {
      main = quantity(figures.used);
    } else {
      main = t('resources.value', {
        used: format.bare(kind, figures.used, unit),
        limit: format.bare(kind, figures.limit, unit),
        unit: format.unit(kind, unit),
      });
    }
    return {
      main,
      requested: quantity(figures.requested),
      limit:
        figures.limit === null
          ? t('resources.unbounded')
          : quantity(figures.limit),
    };
  };
}

/**
 * One resource of a namespace: a bar of what is used against the limit, with
 * the request marked on it, and the request and limit in words under it.
 * Without a bar when there is nothing to draw it against — consumption
 * unknown, or no limit (« illimitée ») — the figures are then said in words
 * only, never as a bar on an invented scale.
 */
export function ResourceMeter(props: {
  kind: ResourceKind;
  label: string;
  figures: ResourceFigures;
  compact?: boolean;
  /** Before the label, from the app vocabulary (components/ui/icons/waadooIcons.ts). */
  icon?: RemixiconComponentType;
}) {
  const { kind, label, figures, compact, icon } = props;
  const format = useUsageFormat();
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const text = useFiguresText()(kind, figures);
  const ratio = usedOfLimit(figures);
  const marker = requestedOfLimit(figures);

  return (
    <Flex direction="column" gap="1">
      {ratio === undefined ? (
        <Flex justify="between" gap="2">
          <Text variant="body-small" truncate>
            {icon ? <IconLabel icon={icon}>{label}</IconLabel> : label}
          </Text>
          <Text variant="body-small" color="secondary">
            {text.main}
          </Text>
        </Flex>
      ) : (
        <Meter
          label={label}
          valueText={text.main}
          ratio={ratio}
          percentText={format.percent(ratio)}
          compact={compact}
          icon={icon}
          marker={
            marker === undefined
              ? undefined
              : {
                  ratio: marker,
                  label: t('resources.requestedMarker', {
                    requested: text.requested,
                  }),
                }
          }
        />
      )}
      <Text variant="body-small" color="secondary">
        {t('resources.details', {
          requested: text.requested,
          limit: text.limit,
        })}
      </Text>
    </Flex>
  );
}

/**
 * One resource of an environment in a cell of the environments table: a short
 * bar and the percentage of the limit in use, the detail — used against the
 * limit, requested, limit, and the volumes of the environment — in a tooltip.
 *
 * The trigger is focusable and named with the main figure, so a keyboard or
 * screen-reader user gets the same reading as a hover: the bar is a `meter`;
 * without one (consumption unknown, limit « illimitée ») an `img` named with
 * the words, never a bar on an invented scale (see `ResourceMeter`).
 */
export function ResourceGauge(props: {
  kind: ResourceKind;
  /** `resources.cpuOf` / `resources.memoryOf`: the resource and the branch. */
  label: string;
  figures: ResourceFigures;
  volumes: VolumeUsage[];
}) {
  const { kind, label, figures, volumes } = props;
  const format = useUsageFormat();
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const text = useFiguresText()(kind, figures);
  const ratio = usedOfLimit(figures);
  const marker = requestedOfLimit(figures);

  const volumesText =
    volumes.length === 0
      ? t('resources.volumesNone')
      : t('resources.volumesTotal', {
          size: format.size(
            volumes.reduce(
              (sum, volume) => sum + (volume.capacityBytes ?? 0),
              0,
            ),
          ),
        });

  let trigger;
  if (ratio === undefined) {
    trigger = (
      <span
        role="img"
        // In the middle of a sentence: the lower-case word when nothing is
        // known, not the tooltip's own « Consommation indisponible ».
        aria-label={t('resources.gaugeLabelNoBar', {
          label,
          value:
            figures.used === null ? t('resources.unavailableShort') : text.main,
        })}
        className="waadoo-env-gauge"
      >
        {figures.used === null ? '—' : text.main}
      </span>
    );
  } else {
    // `Meter`'s tones: amber from 85 %, red from 95 % of the figure shown.
    const tone = GAUGE_TONES[usageLevel(ratio)];
    const percentText = format.percent(ratio);
    trigger = (
      <div
        role="meter"
        // `Focusable` puts it in the tab order (tabIndex 0), so the keyboard
        // reaches the tooltip, which opens on focus; its development check
        // accepts the `meter` and `img` roles on a non-interactive element
        // (react-aria, useFocusable.mjs).
        aria-label={t('resources.gaugeLabel', {
          label,
          value: text.main,
          percent: percentText,
        })}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(displayedPercent(ratio), 100)}
        className="waadoo-env-gauge"
        data-tone={tone}
      >
        {/* The bar of `Meter` (home/Meter.tsx), its compact height, without
            its label row: the column header and the tooltip say it. */}
        <div
          aria-hidden="true"
          className="waadoo-home-meter waadoo-env-gauge__bar"
          data-tone={tone}
          data-compact="true"
        >
          <div
            className="waadoo-home-meter__fill"
            style={{ width: `${Math.min(ratio, 1) * 100}%` }}
          />
          {marker !== undefined && (
            <div
              className="waadoo-home-meter__marker"
              style={{ left: `${Math.min(marker, 1) * 100}%` }}
            />
          )}
        </div>
        <span aria-hidden="true" className="waadoo-env-gauge__percent">
          {percentText}
        </span>
      </div>
    );
  }

  return (
    <TooltipTrigger>
      <Focusable>{trigger}</Focusable>
      <Tooltip>
        <Flex direction="column" gap="1">
          <Text variant="body-small" weight="bold">
            {label}
          </Text>
          <Text variant="body-small">{text.main}</Text>
          <Text variant="body-small">
            {t('resources.details', {
              requested: text.requested,
              limit: text.limit,
            })}
          </Text>
          <Text variant="body-small">{volumesText}</Text>
        </Flex>
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * The cell of `ResourceGauge` when there is nothing to show: a dash, and why
 * in a tooltip — focusable and named like the gauge, so the reason is not
 * reserved to the mouse.
 */
export function ResourceGaugeUnavailable(props: {
  label: string;
  explanation: string;
}) {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return (
    <TooltipTrigger>
      <Focusable>
        <span
          role="img"
          aria-label={t('resources.gaugeLabelNoBar', {
            label: props.label,
            value: t('resources.unavailableShort'),
          })}
          className="waadoo-env-gauge"
        >
          —
        </span>
      </Focusable>
      <Tooltip>{props.explanation}</Tooltip>
    </TooltipTrigger>
  );
}

/** The label of a volume: its guessed role, its claim name otherwise. */
export function useVolumeLabel() {
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  return (volume: VolumeUsage) => {
    if (volume.role === 'attachments') {
      return t('resources.volumeAttachments');
    }
    if (volume.role === 'database') {
      return t('resources.volumeDatabase');
    }
    return volume.name;
  };
}

/** The volumes of a namespace, one per line: role, size, storage class. */
export function VolumeList(props: { volumes: VolumeUsage[] }) {
  const format = useUsageFormat();
  const { t } = useTranslationRef(projectEnvironmentsTranslationRef);
  const labelOf = useVolumeLabel();

  return (
    <Flex direction="column" gap="1">
      <Flex justify="between" gap="2" align="baseline">
        <Text variant="body-small" weight="bold">
          <IconLabel icon={waadooIcons.volume}>
            {t('resources.volumes')}
          </IconLabel>
        </Text>
        <Text variant="body-x-small" color="secondary">
          {t('resources.volumesNote')}
        </Text>
      </Flex>
      {props.volumes.length === 0 ? (
        <Text variant="body-small" color="secondary">
          {t('resources.volumesNone')}
        </Text>
      ) : (
        <ul className="waadoo-resources-volumes">
          {props.volumes.map(volume => (
            <li key={volume.name}>
              <Flex justify="between" gap="2">
                {/* The claim's name as a tooltip: the role is a guess from
                    it, and the name is what `kubectl` and Grafana show. */}
                <Text variant="body-small" title={volume.name} truncate>
                  {labelOf(volume)}
                </Text>
                <Text variant="body-small" color="secondary">
                  {volume.capacityBytes === null
                    ? t('resources.sizeUnknown')
                    : format.size(volume.capacityBytes)}
                  {volume.storageClass ? ` · ${volume.storageClass}` : ''}
                </Text>
              </Flex>
            </li>
          ))}
        </ul>
      )}
    </Flex>
  );
}
